<template>
  <!-- The warning is about the row's settings, so it colours the row rather than
       sitting as a line of text under the name. -->
  <tr :class="['tw:align-middle', hasUnmetRequirements && 'tw:bg-amber-50 tw:dark:bg-amber-950']">
    <td
      class="tw:w-px tw:py-3 tw:pr-2"
      :class="hasUnmetRequirements && 'tw:border-l-2 tw:border-l-amber-400'"
    >
      <span
        class="draggable-handle tw:flex tw:cursor-grab tw:text-muted-foreground"
        :aria-label="$t('Reorder')"
      >
        <Icon name="GripVertical" />
      </span>
    </td>

    <td class="tw:py-3 tw:pr-3">
      <span class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <span class="tw:font-medium">{{ label }}</span>
        <Badge v-if="isExtra" variant="blue">{{ $t('Extra') }}</Badge>
        <Badge v-if="fieldConfig.required" variant="zinc-light">{{ $t('Required to validate') }}</Badge>
        <Badge v-if="fieldConfig.condition" variant="zinc-light">{{ $t('Conditional') }}</Badge>
      </span>

      <!-- The place columns are dropped on a narrow screen, so the same controls sit
           inline under the name, each named by its icon rather than by a header. -->
      <span class="tw:mt-1 tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:md:hidden">
        <FieldStateMenu
          v-for="place in contexts"
          :key="place.ctx"
          :icon="place.icon"
          :model-value="fieldConfig[place.ctx]"
          :read-only="readOnly[place.ctx]"
          @update:model-value="updatePlace(place.ctx, fromFieldState($event))"
        />
      </span>

      <p
        v-for="requirement in unmetRequirements"
        :key="requirement.name"
        class="tw:mt-1 tw:flex tw:items-start tw:gap-2 tw:text-sm tw:text-amber-800 tw:dark:text-amber-200"
      >
        <Icon name="CircleAlert" class="tw:mt-0.5 tw:shrink-0" />
        {{ $t('{field} must also be displayed in: {contexts}', {
          field: requirement.label,
          contexts: requirement.missingIn.join(', '),
        }) }}
      </p>
    </td>

    <td
      v-for="place in contexts"
      :key="place.ctx"
      class="tw:hidden tw:md:table-cell tw:w-32 tw:px-2 tw:py-3 tw:text-center"
    >
      <FieldStateMenu
        fullwidth
        :model-value="fieldConfig[place.ctx]"
        :read-only="readOnly[place.ctx]"
        @update:model-value="updatePlace(place.ctx, fromFieldState($event))"
      />
    </td>

    <td class="tw:w-px tw:py-3 tw:pl-2">
      <Dialog v-model:open="isOpen">
        <DialogTrigger as-child>
          <Button variant="ghost" size="sm" :aria-label="$t('Configure {field}', { field: label })">
            <Icon name="Settings" />
          </Button>
        </DialogTrigger>

        <DialogScrollContent size="lg">
          <DialogHeader>
            <DialogTitle>{{ label }}</DialogTitle>
            <DialogDescription>
              {{ $t('Choose where this field is asked and how it is answered.') }}
            </DialogDescription>
          </DialogHeader>

          <div class="tw:flex tw:flex-col tw:gap-5 tw:py-2">
            <FieldPlaces
              :model-value="fieldConfig"
              :read-only="readOnly"
              @update:place="updatePlace"
            />

            <div class="tw:flex tw:items-center tw:gap-2">
              <Toggle
                :id="`${fieldConfig.name}-field-required`"
                :model-value="fieldConfig.required"
                :disabled="readOnly.required"
                @update:model-value="setRequiredForValidation"
              />
              <label :for="`${fieldConfig.name}-field-required`" class="tw:text-sm">
                {{ $t('Required for a registration to be validated') }}
              </label>
            </div>

            <FieldConditions
              :field-name="fieldConfig.name"
              :model-value="fieldConfig.condition ?? null"
              :sources="conditionSources"
              :effects="conditionEffects ?? []"
              @update:model-value="setCondition"
            />

            <div v-if="settings.length" class="tw:flex tw:flex-col tw:gap-2">
              <TemplateOption
                v-for="setting in settings"
                :key="setting.name"
                :template-option="setting"
                :model-value="fieldConfig.settings?.[setting.name]"
                @update:model-value="onSettingUpdate"
              />
            </div>
          </div>

          <DialogFooter class="tw:sm:justify-between">
            <Button
              v-if="!readOnly.mandatory"
              variant="ghost"
              size="sm"
              class="tw:text-destructive"
              @click="removeField"
            >
              <Icon name="Trash2" />
              {{ $t('Remove') }}
            </Button>
            <DialogClose as-child>
              <Button size="sm">{{ $t('Done') }}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogScrollContent>
      </Dialog>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Toggle from '@/components/ui/Toggle.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/shadcn/components/ui/button'
import { Icon } from '@/components/ui/icon'
import contexts from './contexts'
import FieldPlaces from './FieldPlaces.vue'
import FieldStateMenu from './FieldStateMenu.vue'
import FieldConditions from './FieldConditions.vue'
import TemplateOption from '@/components/form-components/TemplateOption.vue'
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  fromFieldState,
  isExtraField,
  type AvailableField,
  type ConditionChoice,
  type ContextKey,
  type FieldConditionRule,
  type FieldConfig,
  type RequiredField,
  type TemplateOptionDefinition,
  type VisibilityConfig,
} from '@/types/FieldConfig'

interface UnmetRequirement extends RequiredField {
  /** Labels of the places where this field is shown but the required one is not. */
  missingIn: string[];
}

const props = defineProps<{
  fieldConfig: FieldConfig;
  field?: AvailableField;
  /** Every field currently in the form, used to flag unmet requirements. */
  formFields?: FieldConfig[];
  /** Fields in the form that can drive a rule; this one is filtered out. */
  conditionSources?: AvailableField[];
  conditionEffects?: ConditionChoice[];
}>()

const emit = defineEmits<{
  (event: 'remove'): void;
  (event: 'fieldUpdated', config: FieldConfig): void;
}>()

const isOpen = ref(false)

// An extra field removed from the tour settings is no longer in the palette,
// so fall back to its stored name rather than rendering an empty row.
const label = computed<string>(() => props.field?.label ?? props.fieldConfig.name)
const isExtra = computed<boolean>(() => isExtraField(props.fieldConfig.name))
const readOnly = computed(() => props.field?.read_only_configuration ?? {})
const settings = computed<TemplateOptionDefinition[]>(() => props.field?.settings ?? [])

const conditionSources = computed<AvailableField[]>(
  () => (props.conditionSources ?? []).filter(source => source.name !== props.fieldConfig.name)
)

/**
 * A required field has to be displayed wherever this one is: if DIN is asked
 * online, a Birthday only shown in the backoffice leaves the registrant unable
 * to answer it. Mirrors App\Support\Registration\Validation\ValidFormConfig.
 */
const unmetRequirements = computed<UnmetRequirement[]>(() =>
  (props.field?.requires ?? [])
    .map((required: RequiredField) => ({
      ...required,
      missingIn: contexts
        .filter(place => props.fieldConfig[place.ctx]?.display)
        .filter(place => !configFor(required.name)?.[place.ctx]?.display)
        .map(place => place.label),
    }))
    .filter(requirement => requirement.missingIn.length > 0)
)

const hasUnmetRequirements = computed<boolean>(() => unmetRequirements.value.length > 0)

const configFor = (name: string): FieldConfig | undefined =>
  (props.formFields ?? []).find(field => field.name === name)

const updatePlace = (ctx: ContextKey, visibility: VisibilityConfig): void => {
  emit('fieldUpdated', { ...props.fieldConfig, [ctx]: visibility })
}

const setRequiredForValidation = (required: boolean): void => {
  emit('fieldUpdated', { ...props.fieldConfig, required })
}

const setCondition = (condition: FieldConditionRule | null): void => {
  emit('fieldUpdated', { ...props.fieldConfig, condition })
}

const removeField = (): void => {
  isOpen.value = false
  emit('remove')
}

const onSettingUpdate = (setting: { name: string; value: unknown }): void => {
  emit('fieldUpdated', {
    ...props.fieldConfig,
    settings: { ...props.fieldConfig.settings, [setting.name]: setting.value },
  })
}
</script>
